import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/base/SectionHeading";

const services = [
  {
    icon: "ri-body-scan-line",
    title: "Orthopedic and Pain Care",
    description:
      "Orthopedic evaluation and pain-related care, including osteoarthritis treatment, intra-articular injections, platelet-rich plasma procedures, and trigger-point therapy.",
    items: ["Orthopedic consultation", "Osteoarthritis treatment", "Intra-articular injections"],
    tone: "primary",
  },
  {
    icon: "ri-microscope-line",
    title: "Diagnostic Services",
    description:
      "On-site diagnostic testing that supports your care, including laboratory testing, X-rays, sonograms, and electrocardiograms.",
    items: ["Laboratory testing", "X-rays and sonograms", "Electrocardiograms"],
    tone: "accent",
  },
  {
    icon: "ri-heart-pulse-line",
    title: "Wellness Support",
    description:
      "Medically supervised wellness services, including IV vitamin therapy and a weight-management program guided by the clinic.",
    items: ["IV vitamin therapy", "Weight-management program", "Supervised support"],
    tone: "secondary",
  },
];

const toneMap: Record<string, { bubble: string; tag: string }> = {
  primary: { bubble: "bg-primary-100 text-primary-700", tag: "bg-primary-50 text-primary-700" },
  accent: { bubble: "bg-accent-100 text-accent-700", tag: "bg-accent-50 text-accent-700" },
  secondary: {
    bubble: "bg-secondary-100 text-secondary-700",
    tag: "bg-secondary-50 text-secondary-700",
  },
};

export default function ServicesOverview() {
  return (
    <section className="w-full bg-background-50 px-4 md:px-6 py-16 md:py-24">
      <div className="max-w-content mx-auto">
        <Reveal>
          <SectionHeading
            align="center"
            eyebrow="Our services"
            title="Care Built Around Practical Needs"
            description="A range of outpatient services available through one clinic, supported by appropriate diagnostics and clear communication."
          />
        </Reveal>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <Reveal key={service.title} delay={index * 100}>
              <article className="h-full flex flex-col rounded-xl border border-background-200 bg-background-50 p-6 transition-colors duration-200 hover:border-primary-300">
                <span
                  className={`w-12 h-12 rounded-lg flex items-center justify-center ${toneMap[service.tone].bubble}`}
                >
                  <i className={`${service.icon} text-2xl`} />
                </span>
                <h3 className="mt-5 font-heading text-lg font-semibold text-foreground-950">
                  {service.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  {service.description}
                </p>
                <ul className="mt-5 pt-5 border-t border-background-200 space-y-2.5">
                  {service.items.map((item) => (
                    <li key={item} className="flex items-center gap-2.5">
                      <i className="ri-checkbox-circle-line text-primary-500" />
                      <span className="text-sm text-foreground-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <p className="mt-8 text-center text-xs leading-relaxed text-foreground-500 max-w-2xl mx-auto">
            Service availability and suitability are determined by the clinic. This information is
            general and does not represent medical advice or a guaranteed outcome.
          </p>
        </Reveal>
      </div>
    </section>
  );
}